import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { todayInZone, tripPhase } from "@shared/time.ts";
import { formatDateLabel } from "@shared/time.ts";
import { journeyRoute, placesForChapter, placesForDate, stayForDate } from "@shared/itinerary.ts";
import { useTrip, useTripClock } from "../../app/trip.tsx";
import { PlaceActions } from "../../components/PlaceActions.tsx";
import type { Place } from "@shared/schema.ts";
import { verifiedMarkerSignature } from "@shared/place-links.ts";
import "maplibre-gl/dist/maplibre-gl.css";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

export function MapPage() {
  const { trip } = useTrip();
  const [params, setParams] = useSearchParams();
  const selected = params.get("place");
  const host = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const key = import.meta.env.VITE_MAPTILER_KEY;

  const now = useTripClock();
  const phase = trip ? tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone) : "before";
  const today = trip ? todayInZone(now, phase === "before" ? trip.trip.homeTimezone : trip.trip.diaryTimezone) : "";
  const defaultFilter = useMemo(() => {
    if (!trip) return "all";
    if (phase === "during") return `day:${today}`;
    const stay = trip.stays[0];
    return stay ? `chapter:${stay.chapter}` : "all";
  }, [trip, phase, today]);
  const [filter, setFilter] = useState(defaultFilter);

  const places: Place[] = useMemo(() => {
    if (!trip) return [];
    if (filter === "all") return trip.places;
    if (filter.startsWith("day:")) return placesForDate(trip, filter.slice(4));
    if (filter.startsWith("chapter:")) {
      const named = placesForChapter(trip, filter.slice(8));
      return named.length ? named : trip.places.filter((p) => trip.stays.some((s) => s.chapter === filter.slice(8) && s.placeId === p.id));
    }
    return trip.places;
  }, [trip, filter]);

  const verified = places.filter((p) => p.coordinates?.verified);
  const markerKey = verifiedMarkerSignature(verified);
  const selectedPlace = places.find((p) => p.id === selected) ?? trip?.places.find((p) => p.id === selected);

  useEffect(() => {
    if (!key || !host.current) return;
    if (verified.length === 0) return;
    let cancelled = false;
    let map: { remove: () => void } | undefined;
    const snapshot = verified.map((place) => ({
      id: place.id,
      lat: place.coordinates!.lat,
      lng: place.coordinates!.lng,
    }));
    void import("maplibre-gl").then((maplibre) => {
      if (cancelled || !host.current) return;
      maplibre.setWorkerUrl(workerUrl);
      const instance = new maplibre.Map({
        container: host.current,
        style: `https://api.maptiler.com/maps/streets/style.json?key=${key}`,
        center: [snapshot[0].lng, snapshot[0].lat],
        zoom: snapshot.length === 1 ? 12 : 6,
      });
      map = instance;
      instance.on("error", (event) => {
        const message = "error" in event && event.error instanceof Error
          ? event.error.message
          : "Map tiles or the map worker could not load.";
        setMapError(message);
      });
      const bounds = new maplibre.LngLatBounds();
      for (const place of snapshot) {
        const marker = new maplibre.Marker().setLngLat([place.lng, place.lat]).addTo(instance);
        marker.getElement().addEventListener("click", () => {
          setParams({ place: place.id });
        });
        bounds.extend([place.lng, place.lat]);
      }
      if (snapshot.length > 1) instance.fitBounds(bounds, { padding: 48, maxZoom: 12 });
    }).catch(() => setMapError("Map library could not load."));
    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [key, markerKey, setParams, verified.length]);

  if (!trip) return <main className="page">Loading map…</main>;
  const route = journeyRoute(trip);
  const stayToday = stayForDate(trip, today);

  return (
    <main className="page page-wide">
      <h1 className="serif">Places</h1>
      <p className="muted">Straight lines are the plan, not a driving route. Markers appear only for verified coordinates.</p>
      <div className="chip-row" style={{ margin: "12px 0" }}>
        <button className={`chip ${filter === "all" ? "selected" : ""}`} type="button" onClick={() => setFilter("all")}>Whole trip</button>
        {phase === "during" ? (
          <button className={`chip ${filter === `day:${today}` ? "selected" : ""}`} type="button" onClick={() => setFilter(`day:${today}`)}>Today</button>
        ) : null}
        {trip.stays.map((stay) => (
          <button
            key={stay.id}
            className={`chip ${filter === `chapter:${stay.chapter}` ? "selected" : ""}`}
            type="button"
            onClick={() => setFilter(`chapter:${stay.chapter}`)}
          >
            {stay.chapter}
          </button>
        ))}
      </div>
      <nav className="route-strip" aria-label="Itinerary">
        {route.map((stop) => (
          <a
            key={stop.id}
            className={stop.kind === "travel" ? "travel" : ""}
            href={`#${stop.id}`}
            onClick={(e) => {
              e.preventDefault();
              if (stop.kind === "stay") setFilter(`chapter:${stop.label}`);
              else setFilter(`day:${stop.startDate}`);
            }}
          >
            <span className="kicker">{formatDateLabel(stop.startDate)}</span>
            {stop.label}
          </a>
        ))}
      </nav>
      {!key ? <p className="banner">Map unavailable. Addresses and directions still work.</p> : null}
      {key && verified.length === 0 ? <p className="banner" data-map-empty="true">No verified coordinates in this view, so the map stays empty on purpose.</p> : null}
      {mapError ? <p className="banner warn">Map unavailable. Addresses and directions still work.</p> : null}
      {key && verified.length ? <div ref={host} className="map-host" data-map-markers={String(verified.length)} /> : null}
      <div className="timeline">
        {places.map((place) => (
          <article key={place.id} className="card" id={place.id} style={selected === place.id ? { outline: "2px solid var(--brand)" } : undefined}>
            <h2 className="serif" style={{ margin: 0 }}>{place.name}</h2>
            <p>{place.address ?? "Address not on the booking yet."}</p>
            <PlaceActions place={place} compact />
          </article>
        ))}
      </div>
      {selectedPlace ? (
        <>
          <button className="sheet-backdrop" type="button" aria-label="Close place" onClick={() => setParams({})} />
          <aside className="sheet">
            <p className="kicker">{stayToday && selectedPlace.id === stayToday.placeId ? "Tonight" : "Place"}</p>
            <h2 className="serif">{selectedPlace.name}</h2>
            <p>{selectedPlace.address ?? "Area only until a street address is added."}</p>
            <PlaceActions place={selectedPlace} />
            <button className="btn-ghost" type="button" onClick={() => setParams({})}>Close</button>
          </aside>
        </>
      ) : null}
      <p className="muted">Map data attribution appears on the map when tiles load.</p>
    </main>
  );
}
